import type { SortModelItem } from "~/components/grid/AgGrid";
import type { GridRequestInput } from "~/gql/graphqlBase.generated";

export interface SsrmExportFetchContext {
	abortRef: { current: boolean };
	filterModel: unknown;
	onProgress: (current: number, total: number) => void;
	sortModel: SortModelItem[];
	// Set when the caller wants to be told, instead of fetched everything, once the filtered total passes this many rows.
	stopIfTotalExceeds?: number;
}

export interface SsrmExportFetchResult<TRow> {
	cancelled: boolean;
	overLimit: boolean;
	rows: TRow[];
	totalCount: number;
}

// What a full export needs to replay this datasource's query. createSsrmDatasource records it, so the AgGrid wrapper can
// export every filtered row without the page wiring its own export provider.
export interface SsrmExportSource<TRow = unknown, TServerRow = TRow> {
	// For grids that do not load through agGridRows (their own GraphQL block queries): fetch every filtered row.
	fetchAll?: (context: SsrmExportFetchContext) => Promise<SsrmExportFetchResult<TServerRow>>;
	getSearchText?: () => string | undefined;
	isReady?: () => boolean;
	gridId?: string;
	postProcessRows?: (rows: TServerRow[]) => TRow[] | Promise<TRow[]>;
	transformRequest?: (request: GridRequestInput) => GridRequestInput;
}

const exportSourceByDatasource = new WeakMap<object, unknown>();

export const registerSsrmExportSource = <TRow, TServerRow = TRow>(
	datasource: object,
	source: SsrmExportSource<TRow, TServerRow>
): void => {
	exportSourceByDatasource.set(datasource, source);
};

export const getSsrmExportSource = (datasource: unknown): SsrmExportSource<never, never> | undefined => {
	if (typeof datasource !== "object" || datasource === null) return undefined;
	// Heterogeneous sources keep paired callbacks; never prevents callers from supplying mismatched rows.
	return exportSourceByDatasource.get(datasource) as SsrmExportSource<never, never> | undefined;
};

// For pages that wrap a createSsrmDatasource result (for example to gate getRows) and still want the default export.
export const inheritSsrmExportSource = <T extends object>(wrapper: T, wrapped: unknown): T => {
	const source = getSsrmExportSource(wrapped);
	if (source) exportSourceByDatasource.set(wrapper, source);
	return wrapper;
};

// Adapts a block-by-block fetch-all helper to the export's cancel and "too many rows" contract.
export const fetchAllForExport = async <TRow>(
	context: SsrmExportFetchContext,
	run: (args: {
		abortRef: { current: boolean };
		onProgress: (current: number, total: number) => void;
	}) => Promise<{ cancelled: boolean; rows: TRow[]; totalCount: number }>
): Promise<SsrmExportFetchResult<TRow>> => {
	const localAbort = { current: false };
	let overLimit = false;
	const result = await run({
		abortRef: localAbort,
		onProgress: (current, total) => {
			context.onProgress(current, total);
			if (context.abortRef.current) localAbort.current = true;
			if (context.stopIfTotalExceeds != null && total > context.stopIfTotalExceeds) {
				overLimit = true;
				localAbort.current = true;
			}
		}
	});
	return { cancelled: context.abortRef.current, overLimit, rows: result.rows, totalCount: result.totalCount };
};
